<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('report.dealerPayout') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('report.dealerPayout'),
            active: true,
          },
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form class="form-horizontal">
                <fieldset>
                  <legend class="text-black">
                    {{ $t('report.criteriaFilter') }}
                  </legend>
                  <div class="row">
                    <div class="col-sm-12 col-lg-3 col-md-6">
                      <div class="form-group row">
                        <label
                          class="col-md-3 form-col-label control-label required"
                          >{{ $t('report.date') }}</label
                        >
                        <div class="col-md-9">
                          <a-range-picker
                            class="ts-w-full"
                            valueFormat="DD-MM-YYYY"
                            format="DD-MM-YYYY"
                            v-model="model.date"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="userType == 1"
                      class="col-sm-12 col-lg-3 col-md-6"
                    >
                      <div class="form-group row">
                        <label class="col-md-3 form-col-label control-label">{{
                          $t('report.dealer')
                        }}</label>
                        <div class="col-md-9">
                          <a-select
                            style="width: 100%"
                            v-model="model.dealer_id"
                            :options="dealerProfiles"
                            :show-search="true"
                            placeholder="All"
                            :maxTagCount="1"
                            mode="multiple"
                            :filter-option="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div class="validate" v-if="errors.has('dealer_id')">
                            {{ errors.first('dealer_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-sm-12 col-lg-3 col-md-6">
                      <div class="form-group row">
                        <label class="col-md-3 form-col-label control-label">{{
                          $t('report.type')
                        }}</label>
                        <div class="col-md-9">
                          <a-select
                            style="width: 100%"
                            v-model="model.tran_type_id"
                            :options="transaction_types"
                            :show-search="true"
                            placeholder="All"
                            :maxTagCount="1"
                            mode="multiple"
                            :filter-option="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('tran_type_id')"
                          >
                            {{ errors.first('tran_type_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-sm-12 col-lg-3 col-md-6">
                      <div class="form-group row">
                        <div class="col-md-12">
                          <ts-button
                            color="info"
                            class="mr-2"
                            outline
                            :waiting="preview_load"
                            @click.prevent="onPreview"
                            >{{ $t('preview') }}</ts-button
                          >
                          <ts-button
                            color="success"
                            outline
                            :waiting="downloading"
                            @click.prevent="onDownload"
                          >
                            <i class="far fa-file-excel"></i>
                            {{ $t('export') }}
                          </ts-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
          <div id="list" class="ts-overflow-scroll">
            <ts-table
              :columns="columns"
              :records="resources"
              :loading="preview_load"
              :rowGroup="{
                field: 'dealer_name',
              }"
            >
              <template v-slot="{ record }">
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.dealer_code }}
                </td>
                <td class="ts-whitespace-no-wrap">{{ record.tran_date }}</td>
                <td class="ts-whitespace-no-wrap">{{ record.tran_type }}</td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.beginning_balance }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  <span
                    :class="
                      record.transac_amount < 0
                        ? 'ts-text-red-600'
                        : 'ts-text-green-600'
                    "
                    class="ts-font-extrabold"
                    >{{ record.transac_amount }}</span
                  >
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.commission_rate }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.commission }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.ending_balance }}
                </td>
              </template>
            </ts-table>
          </div>
          <ts-card-wrapper>
            <div class="d-flex justify-content-end p-15">
              <ts-pagination
                v-model="pagination"
                @navigate="onPreview"
              ></ts-pagination>
            </div>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import * as FileDownload from 'downloadjs'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
  name: 'dealerPayout',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      preview_load: false,
      downloading: false,
      dealerProfiles: [],
      transaction_types: [],
      model: {
        dealer_id: [],
        tran_type_id: [],
        date: [
          moment().startOf('month').format('DD-MM-YYYY'),
          moment().endOf('month').format('DD-MM-YYYY'),
        ],
      },
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('report/dealerPayout', ['resources', 'pagination']),
    userType() {
      return this.$store.state.authUser.user_type
    },
    columns() {
      return [
        { name: this.$t('report.dealerCode') },
        { name: this.$t('report.tranDate') },
        { name: this.$t('report.tranType'), style: 'text-align: right' },
        {
          name: this.$t('report.beginningBalance'),
          style: 'text-align: right',
        },
        { name: this.$t('report.amount'), style: 'text-align: right' },
        { name: this.$t('report.commissionRate'), style: 'text-align: right' },
        { name: this.$t('report.commission'), style: 'text-align: right' },
        { name: this.$t('report.endingBalance'), style: 'text-align: right' },
      ]
    },
  },
  methods: {
    ...mapActions('report/dealerPayout', [
      'preview',
      'store',
      'fetchDealer',
      'getTransactionType',
    ]),
    async fetchData() {
      this.loading = true
      await this.getTransactionType().then((result) => {
        this.transaction_types = result.data.map((el) => ({
          value: el.tran_type_id,
          label: el.tran_type,
        }))
      })
      await this.fetchDealer()
        .then((response) => {
          this.dealerProfiles = response.data.map((el) => ({
            value: el.dealer_id,
            label: el.dealer_name,
          }))
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onPreview(attributes) {
      this.errors = new Errors()
      this.preview_load = true
      this.preview(
        Object.assign(
          {},
          {
            date_from: this.model.date[0],
            date_to: this.model.date[1],
            dealer_id: this.model.dealer_id,
            tran_type_id: this.model.tran_type_id,
          },
          attributes
        )
      )
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.preview_load = false
        })
    },
    onDownload() {
      this.errors = new Errors()
      this.downloading = true
      this.store({
        date_from: this.model.date[0],
        date_to: this.model.date[1],
        dealer_id: this.model.dealer_id,
        tran_type_id: this.model.tran_type_id,
      })
        .then((response) => {
          FileDownload(response, 'dealer_payout.xlsx')
        })
        .catch((errorData) => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(errorData))
          )
          this.errors = new Errors(errorData.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.downloading = false
        })
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('report/dealerPayout/RESET_STATE')
    next()
  },
}
</script>

<style lang="css" scoped>
#list {
  min-height: 50vh;
  max-height: 60vh;
}
</style>